import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import nivaasLogo from '../../assets/nivaas-logo.png';
import axios from 'axios';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [defaultsettings, setDefaultsettings] = useState([]);
    
  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const settings_response = await axios.get('/api/saas/settings/default-settings');
        setDefaultsettings(settings_response.data);
      } catch (err) { 
        console.log('error');
      }
    };
    
    fetchDefaultSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First, check if the email exists in our system
      console.log('Checking if email exists:', email);
      const checkResponse = await api.post('/api/auth/check-email', { email });
      
      if (!checkResponse.data.exists) {
        setError('No account found with this email address. Please check your email or create a new account.');
        setLoading(false);
        return;
      }

      // If email exists, proceed with password reset
      console.log('Email exists, sending reset request');
      const response = await api.post('/api/auth/forgot-password', { email });
      
      if (response.data.success) {
        setSuccess(true);
        setError('');
      } else {
        setError(response.data.message || 'An error occurred. Please try again.');
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      
      // Handle specific error cases
      if (err.response?.status === 404) {
        setError('No account found with this email address. Please check your email or create a new account.');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.message || 'Invalid email address format.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later or contact support.');
      } else {
        setError(
          err.response?.data?.message || 
          'Unable to process your request. Please check your internet connection and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="container">
        <div className="row justify-content-center mt-5">
          <div className="col-md-6">
            <div className="card shadow">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <img 
                  src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo} 
                  alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                  <div className="mb-3">
                    <i className="fas fa-check-circle" style={{ fontSize: '48px', color: '#28a745' }}></i>
                  </div>
                  <h2 className="mb-3" style={{ color: '#1F458E' }}>Email Sent!</h2>
                  <p className="text-muted mb-4">
                    We&apos;ve sent password reset instructions to <strong>{email}</strong>.
                  </p>
                </div>

                <div className="alert alert-info flex-column">
                  <div className='d-flex'>
                    <i className="fas fa-info-circle"></i>
                    <strong>What&apos;s next?</strong>
                  </div>
                  <ul className="mb-0 mt-2">
                    <li>Check your email inbox (and spam folder)</li>
                    <li>Click the reset link in the email</li>
                    <li>The link will expire in 30 minutes</li>
                  </ul>
                </div>

                <div className="d-grid gap-2">
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                      setError('');
                    }}
                  >
                    <i className="fas fa-envelope me-2"></i>
                    Send Another Email
                  </button>
                  
                  <Link to="/login" className="btn btn-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="row justify-content-center mt-5">
        <div className="col-md-6">
          <div className="card shadow">
            <div className="card-body p-5">
              <div className="text-center mb-4">
                <img 
                src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo} 
                alt="Nivaas Logo" className="mb-3" style={{ maxWidth: '150px', height: 'auto' }} />
                <h2 style={{ color: '#1F458E' }}>Reset Your Password</h2>
                <p className="text-muted">
                  Enter your email address and we&apos;ll send you instructions to reset your password.
                </p>
              </div>
              
              {error && (
                <div className="alert alert-danger flex-column" role="alert">
                  <div className='d-flex'>
                    <i className="fas fa-exclamation-circle"></i>
                    {error}
                  </div>
                  {error.includes('No account found') && (
                    <div className="mt-2">
                      <small>
                        Don&apos;t have an account yet?{' '}
                        <Link to="/register" style={{ color: '#1F458E', fontWeight: '500' }}>
                          Create one here
                        </Link>
                      </small>
                    </div>
                  )}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email address"
                    required
                    disabled={loading}
                  />
                  <small className="text-muted">
                    We&apos;ll send reset instructions to this email address.
                  </small>
                </div>
                
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn py-2"
                    style={{ backgroundColor: '#D67836', color: 'white' }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending Reset Email...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Reset Instructions
                      </>
                    )}
                  </button>
                  
                  <Link to="/login" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Login
                  </Link>
                </div>
              </form>
              
              <div className="mt-4 text-center">
                <small className="text-muted">
                  Remember your password? <Link to="/login" style={{ color: '#1F458E' }}>Sign In</Link>
                </small>
              </div>
            </div>
          </div>
          
          {/* Privacy Policy Footer */}
          <div className="text-center mt-3">
            <small className="text-muted">
              By using this service, you agree to our{' '}
              <Link to="/privacy-policy" className="text-decoration-none" style={{ fontSize: '11px', color: '#1F458E' }}>
                <i className="fas fa-shield-alt me-1"></i>
                Privacy Policy & Terms
              </Link>
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 